import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grow,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { FitAddon } from 'xterm-addon-fit';
import { SearchAddon } from 'xterm-addon-search';
import { XTerm } from 'xterm-for-react';
import { connect } from 'redux-bundler-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useResizeDetector } from 'react-resize-detector';
import { withStyles, useStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TerminalIcon from '@mui/icons-material/Terminal';
import JSZip from 'jszip';

import SsmTunnel from '../../../shared/SsmTunnel';
import AuthorizationCheck from '../../atoms/AuthorizationCheck';
import DcvMetrics from './DcvMetrics';
import DcvMetricsLoadingDialog from './DcvMetricsLoadingDialog';
import Scripts from './Scripts';

const textDecoder = new TextDecoder();
const textEncoder = new TextEncoder();

const styles = (theme, _params, classes) => ({
  content: {
    display: 'flex',
    overflow: 'hidden',
    flexDirection: 'row',
  },
  popoutContent: {
    padding: 0,
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
  },

  buttonSpacer: {
    width: theme.spacing(2),
  },
  spacer: {
    flexGrow: 1,
  },
  dialog: {
    zIndex: 3000,
  },

  closeDialog: {
    zIndex: 4000,
  },
  menu: {
    zIndex: 5000,
  },
  minimizedCard: {
    position: 'fixed',
    padding: theme.spacing(1),
    zIndex: 1000,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition:
      'inset 0.2s,bottom 0.2s, left 0.2s, top 0.2s, width 0.2s, height 0.2s, right 0.2s, opacity 0.2s',
    opacity: 1,
  },
  poppedOutCard: {
    '&&': {},
  },

  popout: {
    right: 0,
    left: 'unset',
    width: '40vw',
    height: '100vh',
  },
  minimized: {
    '&&&': {
      // pointerEvents: 'auto',
      // opacity: 1,
      // right: '8%',
      // width: '150px',
      // height: '40px',
      // bottom: 0,
    },
  },
  minimizedTerminalIcon: {
    marginRight: theme.spacing(1),
  },
});
const consoleStyles = theme => ({
  console: { height: 'auto', width: '100%' },
});

const termOptions = {
  scrollback: 9999999,
  bufferSize: 9999999,
  rows: 34,
  cols: 197,
  fontFamily: 'Fira Code, courier-new, courier, monospace',
  theme: {
    selection: '#f9eb7788',
  },
};

// const fitAddon = new FitAddon();
// const searchAddon = new SearchAddon();

const SsmConsole = withStyles(
  ({
    session,
    classes,
    onConnected,
    onDisconnected,
    parentXtermRef,
    parentSsmDataChannelRef,
    parentSearchAddonRef,
  }) => {
    const xtermRef = React.useRef(null);
    const tunnel = React.useRef(null);

    const fitAddon = new FitAddon();
    const searchAddon = new SearchAddon();

    const onResize = React.useCallback(() => {
      fitAddon.fit();
    }, []);

    const { ref } = useResizeDetector({
      onResize,
      refreshMode: 'debounce',
    });

    const xtermRefCallback = React.useCallback(xterm => {
      if (xterm) {
        xtermRef.current = xterm;
      }

      if (parentXtermRef) {
        parentXtermRef.current = xterm;
      }
    }, []);

    const startStream = () => {
      if (!session.streamUrl) {
        alert('A remote terminal session could not be created');
        return;
      }
      tunnel.current = new SsmTunnel(session);
      // Parent admin tooling (insertCommand + the Commands menu) calls
      // parentSsmDataChannelRef.current.sendText(...); SsmTunnel exposes a
      // sendText alias so that keeps working unchanged.
      parentSsmDataChannelRef.current = tunnel.current;

      tunnel.current.onError(() => {
        alert('A remote terminal session could not be created');
      });

      tunnel.current.onState(state => {
        if (state.type === 'open') {
          onConnected();
        } else if (state.type === 'closed') {
          if (xtermRef.current) {
            xtermRef.current.terminal.write('Session terminated');
          }
          onDisconnected();
        } else if (state.type === 'ready') {
          if (state.customerMessage && xtermRef.current) {
            xtermRef.current.terminal.writeln(state.customerMessage);
          }
          // Terminal geometry is shell-specific, so the consumer sends it
          // (the tunnel stays protocol-agnostic).
          setTimeout(() => tunnel.current.sendControl(termOptions), 500);
        } else if (state.type === 'paused') {
          tunnel.current.sendControl(termOptions);
        } else if (state.type === 'resumed') {
          if (xtermRef.current) {
            xtermRef.current.terminal.focus();
          }
        } else if (state.type === 'resize-request') {
          tunnel.current.sendControl(termOptions);
        }
      });

      tunnel.current.onData(payload => {
        if (xtermRef.current) {
          xtermRef.current.terminal.write(textDecoder.decode(payload));
        }
      });

      tunnel.current.open();
    };

    React.useEffect(() => {
      if (session) startStream();
      if (xtermRef.current) {
        xtermRef.current.terminal.reset();

        fitAddon.activate(xtermRef.current.terminal);
        searchAddon.activate(xtermRef.current.terminal);

        if (parentSearchAddonRef) {
          parentSearchAddonRef.current = searchAddon;
        }
        xtermRef.current.terminal.focus();

        fitAddon.fit();
      }
      return () => {
        if (tunnel.current) {
          tunnel.current.close();
        }
        if (session) {
          session.destroy();
        }
      };
    }, [session]);

    const handleData = async data => {
      if (tunnel.current) {
        tunnel.current.write(textEncoder.encode(data));
      }
    };
    return (
      <div
        ref={ref}
        style={{
          height: '100%',
          overflow: 'hidden',
          minHeight: 0,
          display: 'flex',
          flexGrow: 1,
        }}
      >
        <XTerm
          className={classes.console}
          ref={xtermRefCallback}
          options={termOptions}
          onData={handleData}
          /* addons={[fitAddon, searchAddon]} */
        />
      </div>
    );
  },
  consoleStyles,
);
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Grow ref={ref} {...props} />;
});

const ConfirmCloseDialog = withStyles(
  ({ classes, open, onClose, onConfirm }) => {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.closeDialog}
      >
        <DialogTitle id="alert-dialog-title">
          {'Close Terminal Session?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Closing this terminal session will terminate the session on the
            remote host.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button onClick={onConfirm} color="primary" autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
  styles,
);

const SsmDialog = ({
  classes,
  workstation,
  session,
  open,
  minimized,
  popout,
  onClose,
  onMinimize,
  onPopout,
  onZoom,
  name,
  instanceId,
  activeFacility,
  activeClient,
  onRestartSession,
  platform,
}) => {
  const { cx } = useStyles();
  const [search, setSearch] = React.useState('');
  const [confirmClose, setConfirmClose] = React.useState(false);
  const [connected, setConnected] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showScripts, setShowScripts] = React.useState(false);
  const xtermRef = React.useRef(null);
  const parentSsmDataChannelRef = React.useRef(null);
  const parentSearchAddonRef = React.useRef(null);
  const inputEl = React.useRef(null);
  const dialogRef = React.useRef(null);
  const [dcvMetricsLoading, setDcvMetricsLoading] = React.useState(false);
  const [dcvMetricsLoadingStatus, setDcvMetricsLoadingStatus] = React.useState(
    null,
  );

  const [position, setPosition] = React.useState({
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  });
  useHotkeys(
    'ctrl+/',
    () => inputEl.current.focus(),
    { enableOnFormTags: true },
    [inputEl],
  );

  const [dcvMetrics, setDcvMetrics] = React.useState(null);

  const insertCommand = (command, auto) => {
    parentSsmDataChannelRef.current.sendText(textEncoder.encode(command));

    if (auto) {
      parentSsmDataChannelRef.current.sendText(textEncoder.encode('\r'));
    }

    setTimeout(() => {
      xtermRef.current.terminal.focus();
    }, 200);

    setAnchorEl(null);
  };

  React.useEffect(() => {
    updateDialogPosition();
  }, [open, minimized, popout]);

  const updateDialogPosition = () => {
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      setPosition({
        top: rect.y,
        left: rect.x,
        right: rect.right,
        bottom: rect.bottom,
      });
    }
  };

  const subtitle = workstation ? (
    <Typography variant="body1" className={classes.details}>
      {workstation.name} - {workstation.instanceId} - {workstation.ipAddress}
    </Typography>
  ) : (
    <Typography variant="body1" className={classes.details}>
      {name} - {instanceId}
    </Typography>
  );

  const { width, height, ref } = useResizeDetector({
    onResize: updateDialogPosition,
    refreshMode: 'throttle',
    refreshRate: 100,
  });

  const decodePlainTextOutput = async decompress => {
    console.log(xtermRef.current.terminal.buffer.active.length);
    let text = '';
    for (let i = 0; i < xtermRef.current.terminal.buffer.active.length; i++) {
      text =
        text +
        xtermRef.current.terminal.buffer.active
          .getLine(i)
          .translateToString(true);
    }
    xtermRef.current.terminal.clear();
    text = text.match(/>>>(.*?)<<</g).pop();
    return text.replace(/>>>/, '').replace(/<<</, '');
  };
  const decodeOutput = async decompress => {
    console.log(xtermRef.current.terminal.buffer.active.length);
    let text = '';
    for (let i = 0; i < xtermRef.current.terminal.buffer.active.length; i++) {
      text =
        text +
        xtermRef.current.terminal.buffer.active
          .getLine(i)
          .translateToString(true);
    }
    xtermRef.current.terminal.clear();
    let lastBase64 = text.match(/>>>(.*?)<<</g).pop();
    lastBase64 = lastBase64.replace(/>>>/, '').replace(/<<</, '');
    // console.log(lastBase64);
    if (decompress) {
      const compressedBytes = Uint8Array.from(atob(lastBase64), c =>
        c.charCodeAt(0),
      );

      const decompressedStream = new window.DecompressionStream('gzip');
      const decompressedBlob = new Blob([compressedBytes])
        .stream()
        .pipeThrough(decompressedStream);
      const response = new window.Response(decompressedBlob);
      const text = await response.text();

      console.log(compressedBytes);
      console.log(text);

      return text;
    } else {
      let decodedText = atob(lastBase64);
      // console.log(decodedText);
      return decodedText;
    }
  };
  const getPcoipSupportBundle = async () => {
    setDcvMetricsLoading(true);

    setDcvMetricsLoadingStatus('Generating zip bundle');

    insertCommand(
      `cd C:\\ProgramData\\Teradici\\logs\\ROTATE;
       Remove-Item "C:\\ProgramData\\Teradici\\logs\\ROTATE\\archive.zip" -Force -ErrorAction SilentlyContinue

      Compress-Archive -Path C:\\ProgramData\\Teradici\\logs\\ROTATE\\* -DestinationPath Archive.zip
      $path = 'Archive.zip'; $len = (Get-Item -LiteralPath $path).Length;
      echo ">>>$len<<<"`,
      true,
    );

    await new Promise(resolve => setTimeout(resolve, 15000));
    const size = parseInt(await decodePlainTextOutput());
    console.log('Size: ', size);

    let data = [];
    let offset = 0;
    while (offset < size) {
      insertCommand(
        `$path='C:\\ProgramData\\Teradici\\logs\\ROTATE\\archive.zip'; $offset=${offset}; $maxB64KB=25;
         $rawBytes = [math]::Floor(($maxB64KB*1024)*3/4);
         $fs = [System.IO.File]::Open($path,[IO.FileMode]::Open,[IO.FileAccess]::Read,[IO.FileShare]::ReadWrite)
         try {
           [void]$fs.Seek($offset,[IO.SeekOrigin]::Begin)
           $buf = New-Object byte[] $rawBytes
           $read = $fs.Read($buf,0,$buf.Length)
           if ($read -gt 0) {
             $b64 = [Convert]::ToBase64String($buf,0,$read)
             echo ">>>$b64<<<"
           } else {
             echo ">>><<<"
           }
         } finally { $fs.Dispose() }`,
        true,
      );

      await new Promise(resolve => setTimeout(resolve, 1000));
      const result = await decodeOutput();

      const bytes = Uint8Array.from(result, c => c.charCodeAt(0));
      const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);

      // Convert ArrayBuffer -> hex string
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      data.push(bytes);

      console.log(
        `offset: ${offset} - received ${
          result.length
        } bytes, hash: ${hash} total ${offset + data.length}`,
      );

      if (result == '') break; // EOF sentinel

      const advanced = result.length || 0; // decoded raw bytes in this chunk
      offset += advanced;
      if (advanced === 0) break; // safety: avoid infinite loop
    }

    let blob;

    blob = new Blob(data, { type: 'application/zip' });
    console.log('Blob size: ', blob.size);

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pcoip-bundle.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setDcvMetricsLoading(false);
  };

  const getAgentSupportBundle2 = async () => {
    setDcvMetricsLoading(true);

    setDcvMetricsLoadingStatus('Finding log files...');

    insertCommand(
      `cd c:\\programdata\\mane-agent\\;

      Remove-Item "c:\\programdata\\mane-agent\\archive.zip" -Force -ErrorAction SilentlyContinue

      $TempFolder = "C:\\programdata\\mane-agent\\temp"
      # Ensure temp folder exists (clean it up if it already exists)
      if (Test-Path $TempFolder) {
          Remove-Item $TempFolder -Recurse -Force
      }
      New-Item -ItemType Directory -Path $TempFolder | Out-Null
      Copy-Item c:\\programdata\\mane-agent\\mane-agent-log* -Destination $TempFolder -Force

      Compress-Archive -Path c:\\programdata\\mane-agent\\temp\\* -DestinationPath Archive.zip
      $path = 'Archive.zip'; $len = (Get-Item -LiteralPath $path).Length;
      echo ">>>$len<<<"`,
      true,
    );

    await new Promise(resolve => setTimeout(resolve, 10000));
    const size = parseInt(await decodePlainTextOutput());
    console.log('Size: ', size);

    let data = [];
    let offset = 0;
    while (offset < size) {
      insertCommand(
        `$path='c:\\programdata\\mane-agent\\archive.zip'; $offset=${offset}; $maxB64KB=25;
         $rawBytes = [math]::Floor(($maxB64KB*1024)*3/4);
         $fs = [System.IO.File]::Open($path,[IO.FileMode]::Open,[IO.FileAccess]::Read,[IO.FileShare]::ReadWrite)
         try {
           [void]$fs.Seek($offset,[IO.SeekOrigin]::Begin)
           $buf = New-Object byte[] $rawBytes
           $read = $fs.Read($buf,0,$buf.Length)
           if ($read -gt 0) {
             $b64 = [Convert]::ToBase64String($buf,0,$read)
             echo ">>>$b64<<<"
           } else {
             echo ">>><<<"
           }
         } finally { $fs.Dispose() }`,
        true,
      );

      await new Promise(resolve => setTimeout(resolve, 1000));
      const result = await decodeOutput();

      const bytes = Uint8Array.from(result, c => c.charCodeAt(0));
      const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);

      // Convert ArrayBuffer -> hex string
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      data.push(bytes);

      console.log(
        `offset: ${offset} - received ${
          result.length
        } bytes, hash: ${hash} total ${offset + data.length}`,
      );

      if (result == '') break; // EOF sentinel

      const advanced = result.length || 0; // decoded raw bytes in this chunk
      offset += advanced;
      if (advanced === 0) break; // safety: avoid infinite loop
    }

    let blob;

    blob = new Blob(data, { type: 'application/zip' });
    console.log('Blob size: ', blob.size);

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `arch-bundle-${workstation.name}-${workstation.instanceId}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setDcvMetricsLoading(false);
  };

  const getAgentSupportBundle = async () => {
    setDcvMetricsLoading(true);

    setDcvMetricsLoadingStatus('Finding log files...');

    insertCommand(
      `cd c:\\programdata\\mane-agent\\;

      $files = Get-ChildItem -Path . -Filter "*.log" | Sort-Object LastWriteTime -Descending | Select-Object -ExpandProperty Name;
      $text = ($files -join "\`n");  # Join filenames into a single string with newlines
      $bytes = [System.Text.Encoding]::UTF8.GetBytes($text)
      $base64 = [Convert]::ToBase64String($bytes)  # Convert to Base64
      echo ">>>$base64<<<"`,
      true,
    );

    await new Promise(resolve => setTimeout(resolve, 1000));
    const result = await decodeOutput();
    const files = result.split('\n');

    const data = [];
    let fileIndex = 0;
    for await (const file of files) {
      fileIndex++;

      setDcvMetricsLoadingStatus(
        `Reading log file ${fileIndex} of ${files.length}`,
      );
      insertCommand(
        `
      $text = Get-Content -path "${file}" -raw
      $bytes = [System.Text.Encoding]::UTF8.GetBytes($text)
      # Compress using zlib
      $memoryStream = New-Object System.IO.MemoryStream
      $gzipStream = New-Object System.IO.Compression.GZipStream($memoryStream, [System.IO.Compression.CompressionMode]::Compress)
      $gzipStream.Write($bytes, 0, $bytes.Length)
      $gzipStream.Close()
      $compressedBytes = $memoryStream.ToArray()
      $memoryStream.Close()
      $base64 = [Convert]::ToBase64String($compressedBytes)  # Convert to Base64
      echo ">>>$base64<<<"`,
        true,
      );
      await new Promise(resolve => setTimeout(resolve, 3000));
      const fileData = await decodeOutput(true);
      data.push({ file, fileData });
    }
    const zip = new JSZip();
    data.forEach(({ file, fileData }) => {
      // Ensure each entry has a filename; add ".txt" if you want:
      const name = file?.toString() || 'untitled.txt';
      zip.file(name, fileData ?? '');
    });

    // Build the zip and trigger download
    const blob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bundle.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setDcvMetricsLoading(false);
  };

  const performDcvAnalysis = async () => {
    setDcvMetricsLoading(true);
    setDcvMetricsLoadingStatus('Finding log files...');

    insertCommand(
      `cd c:\\programdata\\nice\\dcv\\log;

      $files = Get-ChildItem -Path . -Filter "server-metrics.*" | Sort-Object LastWriteTime -Descending | Select-Object -ExpandProperty Name;
      $text = ($files -join "\`n");  # Join filenames into a single string with newlines
      $bytes = [System.Text.Encoding]::UTF8.GetBytes($text)
      $base64 = [Convert]::ToBase64String($bytes)  # Convert to Base64
      echo ">>>$base64<<<"`,
      true,
    );

    //wait 10 seconds
    await new Promise(resolve => setTimeout(resolve, 1000));
    const result = await decodeOutput();
    const files = result.split('\n');

    const data = [];
    let fileIndex = 0;
    for await (const file of files) {
      fileIndex++;

      setDcvMetricsLoadingStatus(
        `Reading log file ${fileIndex} of ${files.length}`,
      );
      insertCommand(
        `
      $text = Get-Content -path "${file}" -raw
      $bytes = [System.Text.Encoding]::UTF8.GetBytes($text)
      # Compress using zlib
      $memoryStream = New-Object System.IO.MemoryStream
      $gzipStream = New-Object System.IO.Compression.GZipStream($memoryStream, [System.IO.Compression.CompressionMode]::Compress)
      $gzipStream.Write($bytes, 0, $bytes.Length)
      $gzipStream.Close()
      $compressedBytes = $memoryStream.ToArray()
      $memoryStream.Close()
      $base64 = [Convert]::ToBase64String($compressedBytes)  # Convert to Base64
      echo ">>>$base64<<<"`,
        true,
      );
      await new Promise(resolve => setTimeout(resolve, 3000));
      const fileData = await decodeOutput(true);
      data.push({ file, fileData });
    }

    // insertCommand(
    //   `[convert]::ToBase64String((Get-Content -path "${files[0]}" -Encoding byte)) | %{ ">>>$_<<<" }`,
    //   true,
    // );

    // //wait 10 seconds
    // await new Promise(resolve => setTimeout(resolve, 5000));
    // const data = decodeOutput();
    setDcvMetrics(data);
    setDcvMetricsLoading(false);

    // const blob = new Blob([data], { type: 'text/plain' });
    // const url = window.URL.createObjectURL(blob);
    // const a = document.createElement('a');
    // a.setAttribute('hidden', '');
    // a.setAttribute('href', url);
    // a.setAttribute('download', files[0]);
    // document.body.appendChild(a);
    // a.click();
    // document.body.removeChild(a);

    setTimeout(() => {
      xtermRef.current.terminal.focus();
    }, 200);
  };

  return (
    <>
      {dcvMetricsLoading && (
        <DcvMetricsLoadingDialog open status={dcvMetricsLoadingStatus} />
      )}
      {dcvMetrics && (
        <DcvMetrics
          open={Boolean(dcvMetrics)}
          dcvMetrics={dcvMetrics}
          onClose={() => setDcvMetrics(null)}
        />
      )}
      {confirmClose && (
        <ConfirmCloseDialog
          open={confirmClose}
          onClose={() => setConfirmClose(false)}
          onConfirm={() => {
            onClose();
            setConfirmClose(false);
          }}
        />
      )}

      {open && onMinimize && (
        <Card
          className={cx(minimized && classes.minimized, classes.minimizedCard)}
          style={
            minimized
              ? {
                  top: 'calc(100vh - 40px)',
                  left: '70%',
                  width: 150,
                  height: 40,
                }
              : {
                  height: position.bottom - position.top,
                  width: position.right - position.left,
                  left: position.left,
                  top: position.top,
                }
          }
          onClick={onZoom}
        >
          {minimized && (
            <>
              <TerminalIcon className={classes.minimizedTerminalIcon} />
              Terminal
            </>
          )}
        </Card>
      )}
      <Dialog
        ref={ref}
        maxWidth="xl"
        fullWidth
        fullScreen={!popout}
        aria-labelledby="confirmation-dialog-title"
        open={open && !minimized}
        keepMounted
        hideBackdrop={popout}
        className={popout ? classes.popout : classes.dialog}
        PaperProps={{ ref: dialogRef, variant: 'outlined' }}

        /* onBackdropClick={onClose} */
        /* classes={{ paper: classes.paper }} */
        /* onClose={onClose} */
      >
        {/* <ResizableBox height={400} width={600} className={classes.resizable}> */}
        <DialogTitle className={classes.dialogTitle}>
          <div>
            Remote Terminal Session
            {!popout && subtitle}
          </div>
          <div className={classes.spacer} />

          <Button onClick={e => setAnchorEl(e.currentTarget)}>Commands</Button>

          <div className={classes.buttonSpacer} />
          <TextField
            autoComplete="off"
            id="outlined-required"
            label="Search"
            size="small"
            placeholder="Search"
            variant="outlined"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              inputRef: inputEl,
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                console.log('escape');
                xtermRef.current.terminal.focus();
              }
            }}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                parentSearchAddonRef.current.findPrevious(search, {
                  decorations: { matchBackground: '#f9eb77' },
                });
              }
            }}
          />

          <IconButton
            onClick={() =>
              parentSearchAddonRef.current.findPrevious(search, {
                decorations: { matchBackground: '#f9eb77' },
              })
            }
          >
            <SearchIcon />
          </IconButton>

          {(() => {
            const effectivePlatform =
              platform || workstation?.image?.platform || workstation?.platform;
            const isWindows = effectivePlatform === 'windows';
            const effectiveInstanceId = instanceId || workstation?.instanceId;
            const idleShutdownPath = isWindows
              ? 'C:\\ProgramData\\mane-agent\\disable-idle-shutdown'
              : '/etc/mane-agent/disable-idle-shutdown';
            const disableIdleShutdownCmd = isWindows
              ? `Set-Content -Path "${idleShutdownPath}" -Value "disable"`
              : `touch ${idleShutdownPath}`;
            const enableIdleShutdownCmd = isWindows
              ? `Remove-Item -Path "${idleShutdownPath}" -Force`
              : `rm ${idleShutdownPath}`;
            const ssmLogsCmd = effectiveInstanceId
              ? isWindows
                ? `cd C:\\programdata\\Amazon\\ssm\\InstanceData\\${effectiveInstanceId}\\document\\orchestration`
                : `cd /var/lib/amazon/ssm/${effectiveInstanceId}/document/orchestration`
              : null;

            return (
              <Menu
                className={classes.menu}
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem
                  onClick={() => {
                    setShowScripts(!showScripts);
                    setAnchorEl(null);
                  }}
                >
                  Scripts
                </MenuItem>

                <Divider />

                <MenuItem onClick={() => insertCommand(enableIdleShutdownCmd)}>
                  Enable Idle Shutdown
                </MenuItem>
                <MenuItem onClick={() => insertCommand(disableIdleShutdownCmd)}>
                  Disable Idle Shutdown
                </MenuItem>
                {ssmLogsCmd && (
                  <MenuItem onClick={() => insertCommand(ssmLogsCmd)}>
                    Navigate to SSM Logs
                  </MenuItem>
                )}

                <Divider />

                <AuthorizationCheck permissionName="admin.facility.creation.create">
                  <MenuItem
                    onClick={() => {
                      performDcvAnalysis();
                      setAnchorEl(null);
                    }}
                  >
                    Analyse DCV Metrics
                  </MenuItem>
                </AuthorizationCheck>

                <AuthorizationCheck permissionName="admin.facility.creation.create">
                  <MenuItem
                    onClick={() => {
                      getAgentSupportBundle2();
                      setAnchorEl(null);
                    }}
                  >
                    Get Agent support bundle
                  </MenuItem>
                </AuthorizationCheck>

                <AuthorizationCheck permissionName="admin.facility.creation.create">
                  <MenuItem
                    onClick={() => {
                      getPcoipSupportBundle();
                      setAnchorEl(null);
                    }}
                  >
                    Get PCoIP support bundle
                  </MenuItem>
                </AuthorizationCheck>

                <Divider />

                <ListSubheader>Windows</ListSubheader>
                <MenuItem
                  onClick={() =>
                    insertCommand(
                      "Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\FSLogix\\Profiles' -Name 'ProfileType' -Value 3",
                    )
                  }
                >
                  Enable FSLogix Profile Type to RW/RO fallback (for concurrent
                  connections)
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    insertCommand('Install-WindowsFeature RSAT-ADDS-Tools')
                  }
                >
                  Install AD Tools
                </MenuItem>
                <MenuItem
                  onClick={() => insertCommand('Install-WindowsFeature GPMC')}
                >
                  Install GPMC
                </MenuItem>
                <MenuItem onClick={() => insertCommand('choco install vim -y')}>
                  Install VIM
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    insertCommand(
                      'cd c:\\cinc-project\\cinc\\embedded\\bin; ./irb.bat',
                    )
                  }
                >
                  IRB
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    insertCommand(
                      'Get-Process | Sort-Object -Property CPU -Descending | Select-Object -First 10',
                    )
                  }
                >
                  List top running processes
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    insertCommand('cd c:\\programdata\\mane-agent')
                  }
                >
                  Navigate to Arch Agent Folder
                </MenuItem>
                <MenuItem onClick={() => insertCommand('cd c:\\Programdata')}>
                  Navigate to ProgramData
                </MenuItem>
                <MenuItem onClick={() => insertCommand('nvidia-smi')}>
                  nvidia-smi
                </MenuItem>
                <MenuItem onClick={() => insertCommand('nvidia-smi -q')}>
                  nvidia-smi -q
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    insertCommand(`
 $device = Get-PnpDevice | Where-Object {$_.Description -like "3D Video Controller"}

 # If found, remove the device
 if ($device) {
    Set-ItemProperty -Path  "HKLM:\SOFTWARE\Policies\Microsoft\Windows\DeviceInstall\Settings" -Name "InstallTimeout" -Value 1800 -Type DWord
    Write-Output "Found 3D Video Controller with InstanceId: $($device.InstanceId)"

    # Remove the device
    $removeResult = pnputil /remove-device "$($device.InstanceId)"
    Write-Output "Removal result: $removeResult"

    # Rescan for devices
    $scanResult = pnputil /scan-devices
    Write-Output "Device scan result: $scanResult"
 }`)
                  }
                >
                  Reinitialize graphics card
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    insertCommand(
                      'c:\\programdata\\mane-agent\\mane-agent.exe -service restart',
                    )
                  }
                >
                  Restart Arch Agent
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    insertCommand(
                      "Set-ItemProperty -Path 'HKLM:\\SOFTWARE\\FSLogix\\Profiles' -Name 'ProfileType' -Value 0",
                    )
                  }
                >
                  Set FSLogix Profile Type to default
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    insertCommand('cd c:\\Programdata; cd parsec; cat config.txt')
                  }
                >
                  Show Parsec Config
                </MenuItem>

                <Divider />

                <ListSubheader>Linux</ListSubheader>
                <MenuItem
                  onClick={() =>
                    insertCommand(`
curl -L https://omnitruck.cinc.sh/install.sh | sudo bash -s -- -v 17.10.122
`)
                  }
                >
                  Install Cinc
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    insertCommand(`
        domainNameParameterStore='/AD/Domain/${activeFacility.id}'
        domainJoinUserNameParameterStore='/AD/DomainJoiner/${activeFacility.id}'
        domainJoinPasswordParameterStore='/AD/DomainJoinerPassword/${activeFacility.id}'
        Domain=$(aws --region=us-east-1 ssm get-parameter --name \${domainNameParameterStore} --output text --query Parameter.Value)
        DomainUsername=$(aws --region=us-east-1 ssm get-parameter --name \${domainJoinUserNameParameterStore} --output text --query Parameter.Value)
        DomainPassword=$(aws --region=us-east-1 ssm get-parameter --name \${domainJoinPasswordParameterStore} --with-decryption --output text --query Parameter.Value)
        echo "\${DomainPassword}" | sudo realm join --user="\${DomainUsername}" --client-software=sssd "\${Domain}"
              `)
                  }
                >
                  Join Domain
                </MenuItem>
                <MenuItem onClick={() => insertCommand('ls')}>
                  Show Directory Listing
                </MenuItem>
              </Menu>
            );
          })()}
          <div className={classes.buttonSpacer} />

          {onRestartSession && (
            <IconButton size="small" onClick={onRestartSession}>
              <RefreshIcon />
            </IconButton>
          )}
          {onMinimize && (
            <IconButton size="small" onClick={onMinimize}>
              <MinimizeIcon />
            </IconButton>
          )}

          {onPopout && (
            <IconButton size="small" onClick={onPopout}>
              <OpenInFullIcon />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => (connected ? setConfirmClose(true) : onClose())}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          className={cx(classes.content, popout && classes.popoutContent)}
        >
          <SsmConsole
            session={session}
            onDisconnected={() => setConnected(false)}
            onConnected={() => setConnected(true)}
            parentXtermRef={xtermRef}
            parentSsmDataChannelRef={parentSsmDataChannelRef}
            parentSearchAddonRef={parentSearchAddonRef}
          />
          {showScripts && (
            <Scripts
              platform={platform}
              workstation={workstation}
              xtermRef={xtermRef}
              onCommand={(command, auto) => {
                insertCommand(command, auto);

                setTimeout(() => {
                  xtermRef.current.terminal.focus();
                }, 200);
              }}
            />
          )}
        </DialogContent>
        {/* </ResizableBox> */}
      </Dialog>
    </>
  );
};

export default connect(
  'selectActiveFacility',
  'selectActiveClient',
  'selectRecipeQueryParameter',
  'selectActiveImagePipeline',
  withStyles(SsmDialog, styles),
);
